
import React from 'react';
import { useGuitarMatch } from '../../context/GuitarMatchContext';
import { GUITAR_LEVELS } from '../../types';
import { BASE_TOLERANCE_60BPM, getTimingToleranceMs } from '../../services/severityEngine';

export const AISeverityConfig: React.FC = () => {
    const { currentLevelId, renormalize, lang, targetBpm, appState } = useGuitarMatch();
    const isFr = lang === 'fr';

    const active = GUITAR_LEVELS.find(l => l.id === currentLevelId) || GUITAR_LEVELS[3];

    const currentBpm = targetBpm || 60;
    const realTolerance = getTimingToleranceMs(currentLevelId, currentBpm);
    const baseTolerance60 = BASE_TOLERANCE_60BPM[currentLevelId] || 1000;

    const msPerBeat = Math.round(60000 / currentBpm);
    const msPer8th = Math.round(msPerBeat / 2);

    // Fonction unifiée pour déclencher le recalcul complet avec scanner neural
    const handleLevelChange = (id: number) => {
        if (id === currentLevelId) return;
        // Si on a déjà un résultat, on utilise renormalize pour mettre à jour les scores
        // Sinon (en studio), on change juste l'ID pour les prochaines prises
        renormalize(id);
    };

    return (
        <div className="space-y-8 animate-fade-in pb-10">
            <div className="bg-smart-accent/5 border border-smart-accent/20 p-8 rounded-[40px] space-y-6 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-6 opacity-10 pointer-events-none">
                    <span className="text-6xl italic font-black text-smart-accent">L{active.id}</span>
                </div>
                
                <div className="space-y-1 relative z-10">
                    <h4 className="text-[10px] font-black text-smart-accent uppercase tracking-[0.4em] italic">Configuration Active</h4>
                    <div className="text-3xl font-black text-white uppercase italic tracking-tighter">{active.name}</div>
                </div>

                <div className="grid grid-cols-2 gap-4 relative z-10">
                    <div className="bg-black/40 p-5 rounded-3xl border border-white/5 flex flex-col gap-1 shadow-inner">
                        <span className="text-[8px] font-black text-slate-500 uppercase block mb-1">Fenêtre Réelle</span>
                        <span className="text-2xl font-black text-white italic">±{realTolerance}ms</span>
                        <p className="text-[8px] text-slate-600 font-bold uppercase mt-1">Calculée à {currentBpm} BPM</p>
                    </div>
                    <div className="bg-black/40 p-5 rounded-3xl border border-white/5 flex flex-col gap-1 shadow-inner">
                        <span className="text-[8px] font-black text-slate-500 uppercase block mb-1">Base Yohann</span>
                        <span className="text-2xl font-black text-white italic">±{baseTolerance60}ms</span>
                        <p className="text-[8px] text-slate-600 font-bold uppercase mt-1">Référentiel @60 BPM</p>
                    </div>
                </div>

                <div className="pt-6 border-t border-white/5 relative z-10 space-y-4">
                    <div className="flex justify-between items-center px-2">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest italic">Info Session :</span>
                        <span className="text-[9px] font-mono text-slate-500">Subdiv: x{active.thresholds.subdivisionsPerBeat} | 1 croche = {msPer8th}ms</span>
                    </div>
                    <p className="text-[11px] text-slate-300 font-medium italic leading-relaxed bg-white/[0.02] p-4 rounded-2xl border border-white/5">
                        {isFr 
                            ? `💡 Conseil Coach : En L${active.id}, vous travaillez avec une tolérance de ±${realTolerance}ms. Plus le tempo monte, plus l'IA devient exigeante physiquement.`
                            : `💡 Coach Tip: In L${active.id}, you are working with a ±${realTolerance}ms tolerance. As the tempo increases, the AI becomes physically more demanding.`}
                    </p>
                </div>
            </div>

            <div className="bg-black/40 p-8 rounded-[40px] border border-white/5 space-y-6">
                <div className="space-y-1 px-2">
                    <h4 className="text-xl font-black text-white uppercase italic tracking-tighter">Choisir l'Exigence</h4>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Ajustez la sensibilité du moteur IA</p>
                </div>

                <div className="grid grid-cols-1 gap-3">
                    {GUITAR_LEVELS.map(level => {
                        const isActive = currentLevelId === level.id;
                        const levelBase = BASE_TOLERANCE_60BPM[level.id] || 1000;
                        return (
                            <button 
                                key={level.id}
                                onClick={() => handleLevelChange(level.id)}
                                className={`p-6 rounded-[32px] border flex items-center justify-between transition-all group ${isActive ? 'bg-white border-white scale-[1.02] shadow-2xl' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
                            >
                                <div className="text-left">
                                    <div className={`text-lg font-black uppercase italic tracking-tighter ${isActive ? 'text-black' : 'text-white'}`}>{level.name}</div>
                                    <div className="flex items-center gap-3 mt-1">
                                        <div className={`text-[9px] font-bold uppercase tracking-widest ${isActive ? 'text-slate-500' : 'text-slate-400'}`}>Base @60: ±{levelBase}ms</div>
                                        <div className="w-1 h-1 rounded-full bg-slate-700"></div>
                                        <div className={`text-[9px] font-bold uppercase tracking-widest ${isActive ? 'text-slate-500' : 'text-slate-400'}`}>Subdiv x{level.thresholds.subdivisionsPerBeat}</div>
                                    </div>
                                </div>
                                {isActive ? (
                                    <div className="w-12 h-12 rounded-full bg-smart-accent flex items-center justify-center text-white shadow-xl">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><path d="M20 6L9 17l-5-5"/></svg>
                                    </div>
                                ) : (
                                    <div className="px-6 py-2 rounded-full border border-white/10 text-[8px] font-black text-slate-500 uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">Sélectionner</div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
